/**
 * Settings Page
 * User settings, preferences, account, notifications, and support
 * iOS-first design consistent with JobsPage, MembershipPage
 *
 * @author Varun Pandey
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Settings,
  User,
  Bell,
  Shield,
  HelpCircle,
  Mail,
  ChevronRight,
  LogOut,
  Lock,
  Globe,
  Palette,
  MessageSquare,
  FileText,
  Info,
  Trash2,
  Copy,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useUserStore } from '@/stores/userStore';
import { useAuthStore } from '@/stores/authStore';
import { AuthService } from '@/services/authService';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { Select } from '../../components/ui/Select';
import { Sheet } from '../../components/ui/Sheet';
import { LoggingOutOverlay } from '../../components/ui/LoggingOutOverlay';
import { ResetPasswordModal } from '../../components/features/ResetPasswordModal';
import type { SimilarProfile } from '../../components/features/SimilarProfiles';
import { cn } from '../../utils/cn';
import { isNativeIOSApp } from '@/utils/platform';
import {
  AccountDeletionProvider,
  AccountDeletionOverlays,
  AccountDeletionPendingInline,
  useAccountDeletionFlow,
} from '@/components/features/AccountDeletionFlow';
import { Loader } from '../../components/ui';
import toast from 'react-hot-toast';
import { STORAGE_KEYS } from '../../storage/storageKeys';

const THEME_OPTIONS = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'ta', label: 'Tamil' },
  { value: 'te', label: 'Telugu' },
  { value: 'mr', label: 'Marathi' },
];

/** Row with label and optional right control or chevron (for nav) */
function SettingsRow({
  icon: Icon,
  label,
  subtitle,
  onClick,
  right,
  destructive,
}: {
  icon?: React.ElementType;
  label: string;
  subtitle?: string;
  onClick?: () => void;
  right?: React.ReactNode;
  destructive?: boolean;
}) {
  const content = (
    <>
      {Icon && (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#2C2C2E]">
          <Icon className={cn('h-4 w-4', destructive ? 'text-red-400' : 'text-[#8E8E93]')} />
        </div>
      )}
      <div className="min-w-0 flex-1 flex-col">
        <p className={cn('text-sm font-medium', destructive ? 'text-red-400' : 'text-white')}>
          {label}
        </p>
        {subtitle && <p className="text-xs text-[#8E8E93] mt-0.5">{subtitle}</p>}
      </div>
      {right ?? (onClick && <ChevronRight className="h-5 w-5 text-[#8E8E93] shrink-0" />)}
    </>
  );

  const baseClass =
    'flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1C1C1E]';

  if (onClick && !right) {
    return (
      <button type="button" onClick={onClick} className={cn(baseClass, 'hover:bg-[#2C2C2E]')}>
        {content}
      </button>
    );
  }
  return (
    <div className={cn(baseClass, 'py-3')}>
      {content}
    </div>
  );
}

/** Section card with title and rows */
function SettingsSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
    >
      <h2 className="px-5 pt-5 pb-2 text-xs font-semibold uppercase tracking-wider text-[#8E8E93]">
        {title}
      </h2>
      <div className="divide-y divide-[#3A3A3C]/50">{children}</div>
    </motion.section>
  );
}

/** Delete account row + pending block (native iOS/iPad only — inside AccountDeletionProvider). */
function IosDeleteAccountSettingsRow() {
  const flow = useAccountDeletionFlow();

  const subtitle = flow.hasPendingDeletion
    ? 'Deletion request pending — revoke below'
    : 'Request permanent account deletion';

  return (
    <>
      <SettingsRow
        icon={Trash2}
        label="Delete your account"
        subtitle={subtitle}
        destructive
        onClick={flow.canSubmitDeletion ? flow.openDeleteFlow : undefined}
        right={flow.statusLoading ? <Loader size="small" /> : undefined}
      />
      <AccountDeletionPendingInline />
    </>
  );
}

export const SettingsPage: React.FC = () => {
  const isIos = isNativeIOSApp();
  const navigate = useNavigate();
  const { preferences, setTheme, setLanguage, updateNotificationPreferences, resetPreferences } =
    useUserStore();
  const { user } = useAuthStore();
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    setLogoutConfirmOpen(false);
    setIsLoggingOut(true);
    sessionStorage.setItem(STORAGE_KEYS.SUPPRESS_LOGIN_REDIRECT_TOAST, '1');
    try {
      const success = await AuthService.logout();
      if (success) {
        sessionStorage.setItem(STORAGE_KEYS.LOGOUT_SUCCESS_MESSAGE, 'Logged out successfully.');
        navigate('/login', { replace: true });
      } else {
        sessionStorage.removeItem(STORAGE_KEYS.SUPPRESS_LOGIN_REDIRECT_TOAST);
        toast.error('Logout failed. Please try again.');
      }
    } catch (error) {
      sessionStorage.removeItem(STORAGE_KEYS.SUPPRESS_LOGIN_REDIRECT_TOAST);
      throw error;
    } finally {
      setIsLoggingOut(false);
    }
  };

  const changePasswordProfile: SimilarProfile | null = user
    ? {
      id: user.id,
      stageName: user.name?.trim() || user.email || 'Your account',
      profileImage: user.avatar,
    }
    : null;

  const handleChangePasswordSuccess = async () => {
    setChangePasswordOpen(false);
    toast.success('Password updated. Please sign in again.');
    setIsLoggingOut(true);
    sessionStorage.setItem(STORAGE_KEYS.SUPPRESS_LOGIN_REDIRECT_TOAST, '1');
    try {
      await AuthService.logout();
    } finally {
      setIsLoggingOut(false);
      navigate('/login', { replace: true });
    }
  };

  const handleDeleteAccountInfo = () => {
    toast(
      <div className="text-sm">
        <p className="font-medium">
          Deleting your profile will remove your personal data and settings.
        </p>
        <p className="mt-2">To delete your account drop an email on</p>
        <p className="mt-1 flex items-center gap-2">
          <span className="font-mono text-xs">connect@starclinch.com</span>
          <button
            type="button"
            onClick={() => {
              if (navigator?.clipboard?.writeText) {
                navigator.clipboard.writeText('connect@starclinch.com');
                toast.success('Email copied to clipboard');
              }
            }}
            className="inline-flex items-center gap-1 rounded border border-white/10 px-2 py-0.5 text-[10px] uppercase tracking-wide text-white hover:bg-white/10"
          >
            <Copy className="h-3 w-3" />
            Copy
          </button>
        </p>
      </div>
    );
  };

  const settingsContent = (
    <div className="w-full min-h-screen bg-[#0B0B0D] text-white">
      {isLoggingOut && <LoggingOutOverlay />}
      <div className="w-full md:px-4 py-10 pb-24">
        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-[#3A3A3C]">
              <Settings className="h-5 w-5 text-[#8E8E93]" />
            </div>
            <h1 className="text-2xl lg:text-3xl font-bold text-white">Settings</h1>
          </div>
          <p className="text-[#8E8E93] text-sm lg:text-base">
            Manage your preferences and account
          </p>
        </div>

        <div className="space-y-6 lg:space-y-8">
          {/* Account */}
          <SettingsSection title="Account">
            <SettingsRow
              icon={User}
              label="Edit profile"
              subtitle={user?.email ?? 'Update your profile details'}
              onClick={() => navigate('/profile')}
            />
            <SettingsRow
              icon={Lock}
              label="Change password"
              subtitle="Update your password"
              onClick={() => {
                if (!user) {
                  toast.error('Sign in to change your password');
                  return;
                }
                setChangePasswordOpen(true);
              }}
            />
          </SettingsSection>

          {/* Preferences */}
          <SettingsSection title="Preferences">
            {/* <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#2C2C2E]">
                  <Palette className="h-4 w-4 text-[#8E8E93]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Theme</p>
                  <p className="text-xs text-[#8E8E93] mt-0.5">Light, dark, or system</p>
                </div>
              </div>
              <Select
                options={THEME_OPTIONS}
                value={preferences.theme}
                onChange={(v) => setTheme(v as 'light' | 'dark' | 'system')}
                placeholder="Theme"
                className="w-32 shrink-0"
              />
            </div> */}
            {/* <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#2C2C2E]">
                  <Globe className="h-4 w-4 text-[#8E8E93]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Language</p>
                  <p className="text-xs text-[#8E8E93] mt-0.5">App language</p>
                </div>
              </div>
              <Select
                options={LANGUAGE_OPTIONS}
                value={preferences.language}
                onChange={setLanguage}
                placeholder="Language"
                className="w-32 shrink-0"
              />
            </div> */}
          </SettingsSection>

          {/* Notifications */}
          <SettingsSection title="Notifications">
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#2C2C2E]">
                  <Bell className="h-4 w-4 text-[#8E8E93]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Push notifications</p>
                  <p className="text-xs text-[#8E8E93] mt-0.5">Job alerts and updates</p>
                </div>
              </div>
              <Checkbox
                checked={preferences.notifications.push}
                onCheckedChange={(checked) =>
                  updateNotificationPreferences({ push: checked === true })
                }
              />
            </div>
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#2C2C2E]">
                  <Mail className="h-4 w-4 text-[#8E8E93]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Email notifications</p>
                  <p className="text-xs text-[#8E8E93] mt-0.5">Bookings and reminders</p>
                </div>
              </div>
              <Checkbox
                checked={preferences.notifications.email}
                onCheckedChange={(checked) =>
                  updateNotificationPreferences({ email: checked === true })
                }
              />
            </div>
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#2C2C2E]">
                  <MessageSquare className="h-4 w-4 text-[#8E8E93]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">SMS notifications</p>
                  <p className="text-xs text-[#8E8E93] mt-0.5">OTP and important alerts</p>
                </div>
              </div>
              <Checkbox
                checked={preferences.notifications.sms}
                onCheckedChange={(checked) =>
                  updateNotificationPreferences({ sms: checked === true })
                }
              />
            </div>
          </SettingsSection>

          {/* Privacy & Security */}
          <SettingsSection title="Privacy & Security">
            <SettingsRow
              icon={Shield}
              label="Profile visibility"
              subtitle="Who can see your profile"
              onClick={() => { }}
            />
            <SettingsRow
              icon={Lock}
              label="Two-factor authentication"
              subtitle="Add an extra layer of security"
              onClick={() => { }}
            />
          </SettingsSection>

          {/* Support */}
          <SettingsSection title="Support">
            <SettingsRow
              icon={HelpCircle}
              label="Help centre"
              subtitle="FAQs and guides"
              onClick={() => navigate('/faq')}
            />
            <SettingsRow
              icon={Mail}
              label="Contact us"
              subtitle="Get in touch with support"
              onClick={() => navigate('/contact-us')}
            />
            <SettingsRow
              icon={FileText}
              label="Terms of service"
              subtitle="Usage terms"
              onClick={() => navigate('/terms')}
            />
            <SettingsRow
              icon={FileText}
              label="Privacy policy"
              subtitle="How we use your data"
              onClick={() => { }}
            />
          </SettingsSection>

          {/* About & Data */}
          <SettingsSection title="About & Data">
            <SettingsRow
              icon={Info}
              label="App version"
              right={<span className="text-sm text-[#8E8E93]">4.0.0</span>}
            />
            {isIos ? (
              <IosDeleteAccountSettingsRow />
            ) : (
              <SettingsRow
                icon={Trash2}
                label="Delete your account"
                subtitle="Request permanent account deletion"
                destructive
                onClick={handleDeleteAccountInfo}
              />
            )}
            <SettingsRow
              icon={Trash2}
              label="Clear cache"
              subtitle="Free up storage"
              onClick={() => {
                // TODO: clear cache
              }}
            />
          </SettingsSection>

          {/* Reset preferences */}
          <div className="pt-2">
            <Button
              variant="active"
              className="w-full bg-transparent border border-[#3A3A3C] text-[#8E8E93] hover:bg-[#2C2C2E] hover:text-white"
              onClick={() => resetPreferences()}
            >
              Reset all preferences
            </Button>
          </div>

          {/* Log out */}
          <div className="pb-8">
            <Button
              variant="active"
              className="w-full bg-transparent border border-red-500/50 text-red-400 hover:bg-red-500/10 hover:border-red-500"
              onClick={() => setLogoutConfirmOpen(true)}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Log out
            </Button>
          </div>
        </div>
      </div>

      {/* Logout confirmation sheet */}
      <Sheet open={logoutConfirmOpen} onOpenChange={setLogoutConfirmOpen}>
        <div className="space-y-5 pt-2 w-full">
          <h2 className="text-xl font-bold text-white">Log out?</h2>
          <p className="text-sm text-[#8E8E93]">
            You will need to sign in again to access your account.
          </p>
          <div className="flex gap-3">
            <Button
              variant="active"
              className="flex-1 bg-transparent border border-[#3A3A3C] text-white hover:bg-[#2C2C2E]"
              onClick={() => setLogoutConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="active"
              className="flex-1 bg-red-500 hover:bg-red-600"
              onClick={handleLogout}
            >
              Log out
            </Button>
          </div>
        </div>
      </Sheet>

      {changePasswordProfile && (
        <ResetPasswordModal
          isOpen={changePasswordOpen}
          onClose={() => setChangePasswordOpen(false)}
          profile={changePasswordProfile}
          contactPrefill={{
            email: user?.email,
            phone: user?.phone,
          }}
          flowContext="settings"
          onSuccess={handleChangePasswordSuccess}
        />
      )}

      {isIos && <AccountDeletionOverlays />}
    </div>
  );

  if (isIos) {
    return <AccountDeletionProvider>{settingsContent}</AccountDeletionProvider>;
  }

  return settingsContent;
};
